import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactEcharts from 'echarts-for-react';
import merge from 'lodash.merge';

import Wrapper from 'src/Wrapper';
import { stylePropType } from 'src/PropTypes';
import { Style1 } from 'src/styles';
import { Palette, GradientSelection } from 'src/components';

const TOTAL_COLORS = 22;

function Line({
	labels,
	series,
	style,
	title,
	options,
	onMouseover,
	theme: themeProp,
	onThemeChange,
	gradient: gradientProp,
	onGradientChange,
	usePalette,
	showGradients,
	showMarkline,
	defaultTheme,
}) {
	const [gradient, setGradient] = useState(gradientProp);
	const [theme, setTheme] = useState(themeProp);

	const areaStyleOptions = useMemo(() => {
		if (!usePalette && showGradients) {
			const maxLift = Math.max(...series[0].values);
			const belowOneRange = 1;
			const belowOneTotalColors = (belowOneRange * TOTAL_COLORS) / maxLift - 1;
			const aboveOneTotalColors = Math.abs(TOTAL_COLORS - belowOneTotalColors);
			return {
				areaStyle: {
					color: {
						type: 'linear',
						x: 0,
						y: 0,
						x2: 0,
						y2: 1,
						colorStops: style.COLORS.generateColorStops(belowOneTotalColors, aboveOneTotalColors, gradient),
					},
				},
			};
		}
		return { areaStyle: undefined };
	}, [usePalette, gradient]);

	const seriesList = useMemo(() => {
		const out = series.map((entry) => ({ ...style.Tal.Line.series, ...areaStyleOptions, ...entry }));
		if (showMarkline) out.push({ name: 'Markline', type: 'line', markLine: { ...style.Tal.Line.series.markLine } });
		return out;
	}, [series, showMarkline, areaStyleOptions]);

	const chartOption = useMemo(
		() => ({
			title: {
				text: title,
				...style.global.title,
			},
			textStyle: {
				...style.Tal.Line.textStyle,
			},
			label: {
				...style.Tal.Line.label,
			},
			grid: {
				...style.global.grid,
				...style.Tal.Line.grid,
			},
			backgroundColor: style.global.backgroundColor,
			toolbox: {
				...style.global.toolbox,
			},
			legend: {
				data: series.map(({ name }) => name),
				show: series.length > 1,
				top: 'bottom',
			},
			tooltip: {
				...style.Tal.Line.tooltip,
				formatter: ({ seriesIndex, dataIndex }) => {
					const likelihood = Number(series[seriesIndex].values[dataIndex]);
					const percentile = labels[dataIndex];
					const width = 100 / labels.length;
					return `Percentile bucket: ${percentile - width} - ${percentile} <br /> Lift: ${likelihood.toFixed(1)}`;
				},
			},
			xAxis: {
				...style.Tal.Line.xAxis,
				type: 'category',
				data: labels,
			},
			yAxis: {
				...style.Tal.Line.yAxis,
				type: 'value',
			},
			series: seriesList,
		}),
		[seriesList, title, labels, series]
	);

	const mergedOptions = useMemo(() => ({ ...merge(chartOption, options) }), [options, chartOption]);

	useEffect(() => {
		setGradient(gradientProp);
	}, [gradientProp]);

	useEffect(() => {
		setTheme(themeProp);
	}, [themeProp]);

	if (labels.length === 0 || !series[0].values || series[0].values.length === 0) {
		return null;
	}

	return (
		<Wrapper>
			<ReactEcharts
				ref={(element) => {
					const instance = element ? element.getEchartsInstance() : null;
					if (instance) {
						instance.off('click');
						instance.on('click', 'series', (params) => {
							onMouseover(params);
						});
					}
				}}
				style={{
					width: '100%',
					height: showGradients ? 'calc(100% - 2.5rem)' : '100%',
				}}
				option={mergedOptions}
				notMerge
				theme={theme}
			/>
			<Palette
				hidden={!usePalette}
				defaultTheme={defaultTheme}
				selected={theme}
				onSelect={(newTheme) => {
					setTheme(newTheme);
					onThemeChange(newTheme);
				}}
				style={{
					backgroundColor: mergedOptions.backgroundColor,
					width: '10rem',
					margin: '0 auto',
					paddingTop: '0.5rem',
				}}
			/>
			<GradientSelection
				style={style}
				hidden={!showGradients}
				setGradient={setGradient}
				onGradientChange={onGradientChange}
			/>
		</Wrapper>
	);
}

Line.propTypes = {
	labels: PropTypes.string.isRequired,
	series: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			values: PropTypes.arrayOf(PropTypes.number),
		})
	).isRequired,
	title: PropTypes.string,
	style: stylePropType,
	options: PropTypes.shape({}),
	onMouseover: PropTypes.func,
	theme: PropTypes.string,
	gradient: PropTypes.string,
	onGradientChange: PropTypes.func,
	onThemeChange: PropTypes.func,
	usePalette: PropTypes.bool,
	showMarkline: PropTypes.bool,
	showGradients: PropTypes.bool,
	defaultTheme: PropTypes.shape({}),
};

Line.defaultProps = {
	title: '',
	style: Style1,
	options: {},
	onMouseover: () => null,
	theme: 'pinpoint',
	gradient: Style1.COLORS.GRADIENTS.BLUE_ORANGE,
	onGradientChange: () => null,
	onThemeChange: () => null,
	usePalette: false,
	showGradients: true,
	showMarkline: true,
	defaultTheme: {},
};

export default Line;
